// ============================================================================
// folhaVales: os VALES na linha da folha (tela) (20/10/2026)
// ============================================================================
// Pedido do dono (20/10/2026): o vale (adiantamento) que sai por PIX da Stone
// para quem está na folha é descontado no PRÓXIMO pagamento. Desenho aprovado
// (vales-raiox/DESENHO.md, seção 2c). A folha do mês de PAGAMENTO P pede ao
// servidor (/api/fin op=vales-folha) os vales de cada pessoa:
//   • devidos: saldo > 0 e mês do desconto ≤ P (parcelado: parcelas vencidas
//     até P − o que já foi descontado), sem a fotografia desta competência;
//   • descontados: o que a fotografia VIGENTE desta competência descontou.
// Na linha PENDENTE: vale = menor entre o devido e a Transferência de hoje
// (nunca abaixo de 0), o mais antigo primeiro; nunca sai do cartão benefício
// nem do bônus; Transferência = a de hoje − vale (o título da Transferência
// nasce LÍQUIDO e casa com o PIX); o que não couber fica para o mês seguinte.
// Na linha PAGA: o que a fotografia descontou (não recalcula; reabrir devolve
// o saldo e a linha volta a mostrar o devido).
// O Marcar paga leva componentes.vale + vales [{vale_id, valor_cents}] SEMPRE
// que a chave (vales_folha) do mês estiver ligada; o servidor recalcula e recusa
// a lista diferente (409 vales_mudaram). Chave desligada: o pedido é o de antes.
// A conta é a mesma do servidor (api/_lib/fin-vales.js alocarVales): os testes
// comparam as duas (tests/folha-vales-tela.test.mjs). Centavos inteiros.
// REGRA LINEAR: resposta sem a forma esperada LANÇA (nunca "sem vales").
// Textos sem travessão.
// Revisão (20/10/2026): o vale só entra no pagamento que saiu no dia do PIX ou
// depois (a data do "Quando o dinheiro saiu?": linhaComData refaz a linha e
// diz o que fica para a folha seguinte); a linha paga mostra o que a fotografia
// descontou mesmo com a chave desligada; o vale que espera a baixa do PIX
// aparece na linha (não some calado); sem os vales lidos, a linha diz "vales: ?"
// e o holerite e o CSV ficam travados (motivoSemVales).
// Correção (20/10/2026, revisão final, parte das telas):
//   B1 a linha pendente diz quando OUTRA folha da pessoa está reaberta com vale
//      descontado e o pagamento dela saiu (reabertas_com_vale do servidor): o
//      Marcar paga desta linha com vale é recusado até ela ser marcada de novo;
//      rodada 1 (20/10/2026): na linha que é ela mesma uma folha reaberta
//      (linhas_reabertas do servidor) só as reabertas de mês de pagamento
//      ANTERIOR avisam (marca-se de novo do mês mais antigo para o mais novo; a
//      mesma regra do servidor), então duas reabertas não se travam;
//   M1 depois do Marcar paga, o parcelado que ficou de fora (pelo dia do PIX ou
//      sem a baixa) é dito: as parcelas começam na folha seguinte (avisoValesAdiados);
//   M2 linhaComData converte em centavos com o paraCentavos do folhaContasPagar
//      (o mesmo do pedido e do servidor; Math.round(x * 100) errava 1 centavo em
//      valores como 512,045 e travava a linha com vale maior que a Transferência).
// ============================================================================

import { finGet } from './finApi.js';
// a MESMA conversão de reais em centavos do pedido (montarPedidoFolhaPagar) e do servidor
import { paraCentavos } from './folhaContasPagar.js';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MES_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DIA_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const int = (v) => (Number.isSafeInteger(Number(v)) ? Number(v) : 0);

// 'AAAA-MM' → 'nov/2026'
export function mesAbrevFolha(ym) {
  const s = String(ym || '');
  return MES_RE.test(s) ? `${MESES[Number(s.slice(5, 7)) - 1]}/${s.slice(0, 4)}` : s;
}
export function somarMesFolha(ym, n) {
  if (!MES_RE.test(String(ym || ''))) return '';
  const [y, m] = ym.split('-').map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}
const mesesEntre = (de, ate) => {
  const [y1, m1] = de.split('-').map(Number); const [y2, m2] = ate.split('-').map(Number);
  return (y2 * 12 + m2) - (y1 * 12 + m1);
};
const ddmm = (d) => { const m = DIA_RE.exec(String(d || '')); return m ? `${m[3]}/${m[2]}` : ''; };
const ddmmaaaa = (d) => { const m = DIA_RE.exec(String(d || '')); return m ? `${m[3]}/${m[2]}/${m[1]}` : ''; };
export const reaisVale = (c) => `R$ ${(int(c) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── Leitura ───────────────────────────────────────────────────────────────
// Confere a forma da resposta do op=vales-folha (REGRA LINEAR: forma errada
// lança; nunca vira "ninguém tem vale").
export function validarRespostaValesFolha(j, mesPagamento) {
  if (!j || typeof j !== 'object' || Array.isArray(j)) throw new Error('Resposta inesperada do servidor (vales da folha).');
  if (j.mes_pagamento !== mesPagamento) throw new Error('Os vales vieram de outro mês (atualize a tela).');
  if (typeof j.chave_ligada !== 'boolean') throw new Error('Resposta inesperada do servidor (chave dos vales ausente).');
  if (!j.pessoas || typeof j.pessoas !== 'object' || Array.isArray(j.pessoas)) throw new Error('Resposta inesperada do servidor (lista de vales ausente).');
  for (const p of Object.values(j.pessoas)) {
    if (!p || !Array.isArray(p.devidos)) throw new Error('Resposta inesperada do servidor (vales devidos ausentes).');
    if (p.descontados != null && !Array.isArray(p.descontados)) throw new Error('Resposta inesperada do servidor (vales descontados).');
    if (p.aguardando_baixa != null && !Array.isArray(p.aguardando_baixa)) throw new Error('Resposta inesperada do servidor (vales sem baixa).');
    // correção B1 (20/10/2026): as outras folhas da pessoa reabertas com vale descontado
    if (p.reabertas_com_vale != null && !Array.isArray(p.reabertas_com_vale)) throw new Error('Resposta inesperada do servidor (folhas reabertas com vale).');
    // rodada 1 (20/10/2026): as linhas desta competência que são elas mesmas folhas reabertas
    if (p.linhas_reabertas != null && !Array.isArray(p.linhas_reabertas)) throw new Error('Resposta inesperada do servidor (linhas reabertas).');
  }
  return j;
}
// GET /api/fin?op=vales-folha&mes_pagamento=AAAA-MM (só leitura; o portão é o do Financeiro)
export async function carregarValesFolha(mesPagamento, { get = finGet } = {}) {
  if (!MES_RE.test(String(mesPagamento || ''))) throw new Error('Mês de pagamento inválido.');
  const j = await get('vales-folha', { mes_pagamento: mesPagamento });
  return validarRespostaValesFolha(j, mesPagamento);
}

// ─── Conta da linha ────────────────────────────────────────────────────────
// Espelho do alocarVales do servidor: vale = menor entre o devido e a
// Transferência (nunca abaixo de zero), o mais antigo primeiro (a lista já vem
// nessa ordem). → { vales: [{vale_id, valor_cents}], total_cents, fica_cents }
export function alocarValesFolha(devidos, transferenciaCents) {
  let resta = Math.max(0, Math.floor(Number(transferenciaCents) || 0));
  const vales = [];
  let total = 0; let devidoTotal = 0;
  for (const d of devidos || []) {
    const devido = int(d.devido_cents);
    devidoTotal += devido;
    const v = Math.min(resta, devido);
    if (v > 0) { vales.push({ vale_id: Number(d.vale_id), valor_cents: v }); total += v; resta -= v; }
  }
  return { vales, total_cents: total, fica_cents: devidoTotal - total };
}

const infoDoVale = (d) => ({
  vale_id: Number(d.vale_id),
  dia: String(d.dia || '').slice(0, 10),
  // o devido traz o valor do vale em valor_cents; o descontado, em valor_total_cents
  valor_total_cents: int(d.valor_total_cents ?? d.valor_cents),
  parcelas: int(d.parcelas) || 1,
  mes_desconto: d.mes_desconto || null,
  conta: d.conta || null,
});

// Os vales de UMA linha da folha.
//   dados = resposta do op=vales-folha (ou null enquanto não carregou)
//   status = 'paga' | 'pendente'; transferenciaCents = a Transferência de hoje SEM o vale
//   ateDia (revisão 20/10/2026) = a data deste pagamento: o vale com o PIX depois dela fica de fora
// → { chave, vales: [{vale_id, valor_cents, dia, valor_total_cents, parcelas, mes_desconto, conta}],
//     total_cents, fica_cents, fonte: 'devidos' | 'fotografia' | null,
//     depois: [os vales com o PIX depois da data], aguardando: [os que esperam a baixa do PIX],
//     reabertas: [as OUTRAS folhas da pessoa reabertas com vale descontado (correção B1); só na pendente] }
export function valesDaLinha({ dados, mesPagamento, professionalId, employeeId = null, status, transferenciaCents, ateDia = null }) {
  const doMes = !!dados && dados.mes_pagamento === mesPagamento;
  const chave = doMes && dados.chave_ligada === true;
  const vazio = { chave, vales: [], total_cents: 0, fica_cents: 0, fonte: null, depois: [], aguardando: [], reabertas: [] };
  if (!doMes || !professionalId) return vazio;
  const p = dados.pessoas?.[professionalId];
  if (!p) return vazio;
  const descontados = Array.isArray(p.descontados) ? p.descontados : [];
  const desta = (d) => !employeeId || d.employee_id == null || String(d.employee_id) === String(employeeId);
  // chave desligada: só a linha PAGA, com o que a fotografia descontou (fato gravado; o holerite não perde o vale)
  if (!chave) {
    if (status !== 'paga') return vazio;
    const vales = descontados.filter(desta).map(d => ({ ...infoDoVale(d), valor_cents: int(d.valor_cents) })).filter(v => v.valor_cents > 0);
    return { ...vazio, vales, total_cents: vales.reduce((a, v) => a + v.valor_cents, 0), fonte: vales.length ? 'fotografia' : null };
  }
  const aguardando = status === 'paga' ? [] : (Array.isArray(p.aguardando_baixa) ? p.aguardando_baixa : [])
    .map(a => ({ vale_id: Number(a.vale_id), dia: String(a.dia || '').slice(0, 10), valor_cents: int(a.valor_cents), conta: a.conta || null }));
  const ate = /^\d{4}-\d{2}-\d{2}$/.test(String(ateDia || '')) ? String(ateDia) : null;
  // a mesma pessoa com outra linha da folha (outro cadastro de funcionário) que já
  // descontou nesta competência: o servidor conta esse desconto (só exclui a
  // fotografia DESTA linha), então o devido daqui é o resto
  const outras = new Map();
  for (const d of descontados) if (!desta(d)) outras.set(Number(d.vale_id), (outras.get(Number(d.vale_id)) || 0) + int(d.valor_cents));
  const devidos0 = (Array.isArray(p.devidos) ? p.devidos : [])
    .map(d => (outras.has(Number(d.vale_id)) ? { ...d, devido_cents: Math.max(0, int(d.devido_cents) - outras.get(Number(d.vale_id))) } : d))
    .filter(d => int(d.devido_cents) > 0);
  // o PIX de depois deste pagamento fica para a folha seguinte (o servidor faz a mesma conta)
  const depois = status === 'paga' || !ate ? [] : devidos0.filter(d => String(d.dia || '').slice(0, 10) > ate).map(d => ({ ...infoDoVale(d), devido_cents: int(d.devido_cents) }));
  const devidos = depois.length ? devidos0.filter(d => !(String(d.dia || '').slice(0, 10) > ate)) : devidos0;
  const devidoTotal = devidos.reduce((a, d) => a + int(d.devido_cents), 0);
  if (status === 'paga') {
    const vales = descontados.filter(desta).map(d => ({ ...infoDoVale(d), valor_cents: int(d.valor_cents) })).filter(v => v.valor_cents > 0);
    const total = vales.reduce((a, v) => a + v.valor_cents, 0);
    // devido do mês que esta fotografia não descontou: vai para o mês seguinte sozinho
    return { chave, vales, total_cents: total, fica_cents: Math.max(0, devidoTotal - total), fonte: 'fotografia', depois: [], aguardando: [], reabertas: [] };
  }
  const a = alocarValesFolha(devidos, transferenciaCents);
  const vales = a.vales.map(v => ({ ...infoDoVale(devidos.find(d => Number(d.vale_id) === v.vale_id) || v), valor_cents: v.valor_cents }));
  // correção B1 (20/10/2026): outra folha da pessoa reaberta com vale descontado e o pagamento dela saído
  // (o servidor já tira a competência desta tela): a linha avisa e o servidor recusa o Marcar paga com vale.
  // Rodada 1: esta linha é ela mesma uma folha reaberta? Então só as de mês de pagamento anterior travam
  const propria = !!employeeId && (Array.isArray(p.linhas_reabertas) ? p.linhas_reabertas : []).some(e => String(e) === String(employeeId));
  const reabertas = (Array.isArray(p.reabertas_com_vale) ? p.reabertas_com_vale : [])
    .filter(r => r && MES_RE.test(String(r.mes_pagamento || '')))
    .filter(r => !propria || String(r.mes_pagamento) < String(mesPagamento))
    .map(r => ({ employee_id: r.employee_id ?? null, competencia: r.competencia || null, mes_pagamento: String(r.mes_pagamento) }));
  return { chave, vales, total_cents: a.total_cents, fica_cents: a.fica_cents, fonte: 'devidos', depois, aguardando, reabertas };
}

// A linha PENDENTE com a data do pagamento escolhida no "Quando o dinheiro saiu?" (revisão
// 20/10/2026): refaz os vales só com o PIX até essa data (a mesma conta do servidor) e devolve a
// linha nova (Transferência, líquido e vale) e os vales que ficam para a folha seguinte.
// → { row, depois: [{ vale_id, dia, devido_cents, … }], mudou }
export function linhaComData(row, { dados, mesPagamento, dataPagamento }) {
  if (!row || !row.valesChave || row.status === 'paga' || !/^\d{4}-\d{2}-\d{2}$/.test(String(dataPagamento || ''))) {
    return { row, depois: [], mudou: false };
  }
  // correção M2 (20/10/2026): o paraCentavos do pedido e do servidor (Math.round(x * 100) dá 51204 em 512,045)
  const tSem = paraCentavos(row.transferenciaSemVale);
  const vl = valesDaLinha({ dados, mesPagamento, professionalId: row.emp?.professionalId, employeeId: row.emp?.id,
    status: 'pendente', transferenciaCents: tSem, ateDia: dataPagamento });
  const igual = vl.vales.length === (row.valesLinha || []).length
    && vl.vales.every(v => (row.valesLinha || []).some(x => Number(x.vale_id) === v.vale_id && int(x.valor_cents) === v.valor_cents));
  if (igual) return { row, depois: vl.depois, mudou: false };
  const vale = vl.total_cents / 100;
  const novo = {
    ...row, vale, valeCents: vl.total_cents, valesLinha: vl.vales, valeFicaCents: vl.fica_cents,
    transferencia: (tSem - vl.total_cents) / 100,
    liquido: (paraCentavos(row.liquido) + paraCentavos(row.vale) - vl.total_cents) / 100,
    valesReabertas: vl.reabertas,
  };
  return { row: novo, depois: vl.depois, mudou: true };
}
// "Vale de 06/11 saiu depois deste pagamento: fica para dez/2026"
export function avisoValeDepois(depois, mesPagamento) {
  const l = Array.isArray(depois) ? depois : [];
  if (!l.length) return null;
  const dias = l.map(d => ddmm(d.dia)).filter(Boolean).join(', ');
  return `${l.length > 1 ? 'Vales de' : 'Vale de'} ${dias} ${l.length > 1 ? 'saíram' : 'saiu'} depois deste pagamento: ${l.length > 1 ? 'ficam' : 'fica'} para ${mesAbrevFolha(somarMesFolha(mesPagamento, 1))}`;
}
// "vale de 03/11 (R$ 300,00) espera a baixa do PIX"
export function avisoValeAguardando(a) {
  return `vale de ${ddmm(a.dia)} (${reaisVale(a.valor_cents)}) espera a baixa do PIX`;
}
export const DICA_VALE_AGUARDANDO = 'Vale lançado pela Nova conta: só entra na folha depois do "Vincular e dar baixa" do extrato (vale sem PIX não existe).';
// correção B1 (20/10/2026): "a folha de nov/2026 desta pessoa está reaberta com vale descontado: marque-a de novo antes"
export function avisoFolhaReabertaComVale(reabertas) {
  const l = Array.isArray(reabertas) ? reabertas.filter(r => MES_RE.test(String(r?.mes_pagamento || ''))) : [];
  if (!l.length) return null;
  const meses = [...new Set(l.map(r => mesAbrevFolha(r.mes_pagamento)))];
  return meses.length > 1
    ? `as folhas de ${meses.join(', ')} desta pessoa estão reabertas com vale descontado: marque-as de novo antes`
    : `a folha de ${meses[0]} desta pessoa está reaberta com vale descontado: marque-a de novo antes`;
}
// no reabrir de uma linha PAGA que descontou vale, com o pagamento saído (a data fica guardada): a folha
// precisa ser marcada de novo antes da seguinte desta pessoa (correção B1, 20/10/2026)
export function avisoReabrirComVale({ valesLinha = [], pagamentoNaoSaiu = false } = {}) {
  const total = (Array.isArray(valesLinha) ? valesLinha : []).reduce((a, v) => a + Math.max(0, int(v?.valor_cents)), 0);
  if (pagamentoNaoSaiu || !(total > 0)) return null;
  return `O vale descontado nesta folha (${reaisVale(total)}) já saiu no pagamento: marque esta folha de novo antes de pagar a folha seguinte desta pessoa.`;
}
export const DICA_FOLHA_REABERTA_COM_VALE = 'O pagamento daquela folha já saiu com o vale descontado. Enquanto ela estiver reaberta, o Marcar paga desta linha com vale é recusado (senão o vale sai duas vezes).';
// correção M1 (20/10/2026): o parcelado que ficou de fora deste pagamento (resposta vales_adiados do
// Marcar paga) começa na folha seguinte. info(vale_id) = { dia } do vale (os devidos e os que esperam a
// baixa da tela); ateDia = a data do pagamento (a resposta da SQL não traz o motivo: o PIX depois da data
// é 'pix_depois', senão 'sem_baixa', a mesma regra do passo 1c).
// "Vale parcelado de 06/11 saiu depois deste pagamento: as parcelas começam na folha de dez/2026"
export function avisoValesAdiados(adiados, { info = () => null, ateDia = null } = {}) {
  const l = (Array.isArray(adiados) ? adiados : []).filter(a => a && MES_RE.test(String(a.para || '')));
  if (!l.length) return null;
  const ate = /^\d{4}-\d{2}-\d{2}/.test(String(ateDia || '')) ? String(ateDia).slice(0, 10) : null;
  return l.map(a => {
    const dia = String(info(Number(a.vale_id))?.dia || '').slice(0, 10);
    const d = ddmm(dia);
    const motivo = a.motivo || (ate && d ? (dia > ate ? 'pix_depois' : 'sem_baixa') : null);
    const quem = `Vale parcelado${d ? ` de ${d}` : ''}`;
    const porque = motivo === 'sem_baixa' ? 'ainda sem a baixa do PIX' : motivo === 'pix_depois' ? 'saiu depois deste pagamento' : 'ficou fora deste pagamento';
    return `${quem} ${porque}: as parcelas começam na folha de ${mesAbrevFolha(a.para)}`;
  }).join('; ');
}
// os vales que a tela conhece de uma pessoa (devidos, os que esperam a baixa e os descontados), por id
export function infoDosValesDaPessoa(dados, professionalId) {
  const p = dados?.pessoas?.[professionalId];
  const todos = p ? [...(p.devidos || []), ...(p.aguardando_baixa || []), ...(p.descontados || [])] : [];
  return (id) => todos.find(v => Number(v?.vale_id) === Number(id)) || null;
}
// Sem a lista de vales do mês: a linha diz "vales: ?" e o holerite e o CSV ficam travados (REGRA LINEAR)
export function motivoSemVales(carga, mesPagamento) {
  if (!carga || carga.mes !== mesPagamento) return 'os vales do mês ainda estão carregando';
  if (carga.estado === 'erro') return `a lista de vales do mês não carregou (${carga.erro || 'erro'})`;
  if (carga.estado !== 'ok') return 'os vales do mês ainda estão carregando';
  return null;
}

// Depois do "Marcar paga" ok: a fotografia desta linha descontou `vales`
// (a lista que o servidor gravou); reabrir = lista vazia. Nova cópia de `dados`
// (a tela não espera a releitura para mostrar a linha certa).
export function registrarDescontados(dados, { professionalId, employeeId, vales = [] }) {
  if (!dados || !professionalId || !dados.pessoas) return dados;
  const p = dados.pessoas[professionalId] || { devidos: [], total_devido_cents: 0, descontados: [] };
  const outros = (p.descontados || []).filter(d => employeeId && d.employee_id != null && String(d.employee_id) !== String(employeeId));
  const info = (id) => (p.devidos || []).find(d => Number(d.vale_id) === Number(id)) || (p.descontados || []).find(d => Number(d.vale_id) === Number(id)) || { vale_id: id };
  const novos = (vales || []).filter(v => int(v.valor_cents) > 0).map(v => {
    const i = infoDoVale(info(v.vale_id));
    return { vale_id: i.vale_id, employee_id: employeeId || null, valor_cents: int(v.valor_cents), dia: i.dia,
      valor_total_cents: i.valor_total_cents, parcelas: i.parcelas, mes_desconto: i.mes_desconto, conta: i.conta };
  });
  const descontados = [...outros, ...novos];
  return {
    ...dados,
    pessoas: { ...dados.pessoas, [professionalId]: { ...p, descontados, total_descontado_cents: descontados.reduce((a, d) => a + d.valor_cents, 0) } },
  };
}

// ─── Textos da linha (sem travessão) ───────────────────────────────────────
// "−R$ 670,00 vale 14/10" (embaixo do Desconto, no estilo de "−R$ X faltas") e a dica
export function rotuloValeFolha(v, { mesPagamento } = {}) {
  const N = int(v.parcelas) || 1;
  const partes = [`Vale de ${ddmmaaaa(v.dia)}: ${reaisVale(v.valor_total_cents ?? v.valor_cents)}`];
  if (N > 1) {
    const n = MES_RE.test(String(v.mes_desconto || '')) && MES_RE.test(String(mesPagamento || ''))
      ? Math.min(N, Math.max(1, mesesEntre(v.mes_desconto, mesPagamento) + 1)) : null;
    partes.push(n ? `em ${N} parcelas (vence até a ${n}ª nesta folha)` : `em ${N} parcelas`);
  }
  let detalhe = `${partes.join(' ')}. Nesta folha: ${reaisVale(v.valor_cents)}.`;
  if (v.conta) detalhe += ` PIX pela conta ${v.conta}.`;
  return { linha: `−${reaisVale(v.valor_cents)} vale ${ddmm(v.dia)}`, detalhe };
}
// "R$ 330,00 de vale fica para dez/2026"
export function avisoValeFica(ficaCents, mesPagamento) {
  if (!(int(ficaCents) > 0)) return null;
  return `${reaisVale(ficaCents)} de vale fica para ${mesAbrevFolha(somarMesFolha(mesPagamento, 1))}`;
}
// transição: a linha tem vale e também Desconto manual
export const AVISO_DESCONTO_E_VALE = 'Confira se o Desconto não é o mesmo vale';
export function avisoDescontoEVale(descontoManual, valeCents) {
  return int(valeCents) > 0 && Math.abs(Number(descontoManual) || 0) > 0.004 ? AVISO_DESCONTO_E_VALE : null;
}
// comissão do Avec negativa: sinal de vale lançado no Avec para quem está na folha
// (decisão 1 do dono, 20/10/2026: esse vale é lançado só aqui)
export function avisoComissaoAvecNegativa(valorReais) {
  const v = Number(valorReais) || 0;
  if (!(v < 0)) return null;
  return {
    linha: `Avec −${reaisVale(Math.round(-v * 100))}`,
    detalhe: `A comissão do Avec deste mês veio negativa (−${reaisVale(Math.round(-v * 100))}): confira se há vale lançado no Avec. O vale de quem está na folha é lançado só aqui, pela linha do extrato Stone.`,
  };
}
// a menor comissão do Avec da pessoa no mês (fk_performance), só quando nenhuma é positiva
export function comissaoAvecNegativa(performance, { professionalId, year, month }) {
  if (!professionalId) return 0;
  const vs = (performance || []).filter(p => p && p.professionalId === professionalId && p.year === year && p.month === month)
    .map(p => Number(p.comissao) || 0);
  if (!vs.length || vs.some(v => v > 0)) return 0;
  const min = Math.min(...vs);
  return min < 0 ? min : 0;
}

// ─── Recibo e holerite ─────────────────────────────────────────────────────
// Vales da linha → itens do recibo e do holerite (linha "24 Adiantamento (vale)")
// [{ dia, valor (reais), referencia: 'PIX 14/10' }]
export function valesParaRecibo(vales) {
  return (Array.isArray(vales) ? vales : []).filter(v => int(v?.valor_cents) > 0).map(v => ({
    dia: String(v.dia || '').slice(0, 10), valor: int(v.valor_cents) / 100, referencia: v.dia ? `PIX ${ddmm(v.dia)}` : '',
  }));
}
